import {
  BulkActionsDropdown as ComponentBulkActionsDropdown,
  ConfirmModal,
  useTableRowSelection,
} from '@/components';
import { useMessage } from '@/providers';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { z } from 'zod';

import { useBulkUpdateCommentsStatus } from '@/features/comment/hooks/comment';
import { useCommentListSearchQueryStates } from '@/features/comment/hooks/search-query-states';
import { commentBulkUpdateStatusActionType } from '@/features/comment/models/comment';

type BulkUpdateStatusType = z.infer<typeof commentBulkUpdateStatusActionType>;
const bulkType = z.enum([
  ...commentBulkUpdateStatusActionType.options,
  'bulk-archive',
]);

type BulkType = z.infer<typeof bulkType>;

export const CommentListTableBulkActionsDropdown: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { mutateAsync, isPending } = useBulkUpdateCommentsStatus();
  const { selectedRowKeys, clearAllSelections } = useTableRowSelection();
  const [selectedModalKey, setSelectedModalKey] = useState<BulkType | null>(
    null,
  );

  const [{ page }, setSearchQueryStates] = useCommentListSearchQueryStates();

  const handleCloseModal = (): void => {
    setSelectedModalKey(null);
  };

  const handleBulkUpdate = async (key: BulkType) => {
    const res = await mutateAsync({
      action: commentBulkUpdateStatusActionType.parse(key),
      commentIds: selectedRowKeys as string[],
    });

    openMessage(
      'success',
      t('message-bulk-operation-completed-with-total-and-changed-count', {
        total: selectedRowKeys.length,
        changedCount: res.targetCount - res.skippedIds.length,
      }),
    );
    clearAllSelections();
    handleCloseModal();
    if (page > 1) {
      setSearchQueryStates((prev) => ({ ...prev, page: 1 }));
    }
  };

  const confirmMessageTargetAction = selectedModalKey
    ? (
        {
          'read-and-resolved': t('resolved'),
          unresolved: t('unresolved'),
          read: t('comment.read'),
          unread: t('comment.unread'),
        } satisfies Record<BulkUpdateStatusType, string>
      )[selectedModalKey]
    : undefined;

  return (
    <>
      <ComponentBulkActionsDropdown
        disabled={selectedRowKeys.length === 0}
        menu={{
          items: [
            {
              key: 'read-and-resolved' satisfies BulkType,
              label: t('comment.mark-as-read-and-resolved'),
            },
            {
              key: 'unresolved' satisfies BulkType,
              label: t('comment.mark-as-unresolved'),
            },
            {
              key: 'read' satisfies BulkType,
              label: t('comment.mark-as-read'),
            },
            {
              key: 'unread' satisfies BulkType,
              label: t('comment.mark-as-unread'),
            },
          ],
          onClick: ({ key }) => {
            setSelectedModalKey(bulkType.parse(key));
          },
        }}
      />
      <ConfirmModal
        open={
          selectedModalKey !== null &&
          commentBulkUpdateStatusActionType.options.includes(selectedModalKey)
        }
        title={t('comment.bulk-operation')}
        disclaimerText={t(
          'comment.confirm-message-for-bulk-operation-with-target-count-and-target-action',
          {
            targetCount: selectedRowKeys.length,
            targetAction: confirmMessageTargetAction,
          },
        )}
        confirmLoading={isPending}
        onOk={async () => {
          invariant(
            selectedModalKey !== null &&
              commentBulkUpdateStatusActionType.options.includes(
                selectedModalKey,
              ),
            t('unreachable-code'),
          );

          handleBulkUpdate(selectedModalKey);
        }}
        onCancel={handleCloseModal}
        skipCheck
      />
    </>
  );
};
